import { useState, type ReactElement } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  CheckmarkCircleFilled,
  CloudRegular,
  CodeRegular,
  DocumentRegular,
  ServerRegular,
  ShieldCheckmarkFilled,
  WarningFilled,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import type { SbomComponent, SbomVuln } from '../api/types';

const COMMERCIAL_META: Record<string, { label: string; color: string; icon: ReactElement }> = {
  ok: { label: 'Erlaubt', color: '#1e7d34', icon: <CheckmarkCircleFilled /> },
  cond: { label: 'Mit Auflagen', color: '#9a6700', icon: <WarningFilled /> },
  service: { label: 'Kostenpflichtiger Dienst', color: '#2a5bb8', icon: <CloudRegular /> },
};

const ICONS: Record<string, ReactElement> = {
  code: <CodeRegular />,
  server: <ServerRegular />,
  document: <DocumentRegular />,
  cloud: <CloudRegular />,
};

const SEV_COLOR: Record<string, 'danger' | 'warning' | 'informative' | 'subtle'> = {
  critical: 'danger',
  high: 'warning',
  moderate: 'informative',
  low: 'subtle',
};
const SEV_LABEL: Record<string, string> = { critical: 'Kritisch', high: 'Hoch', moderate: 'Mittel', low: 'Niedrig' };

function CommercialCell({ value }: { value: string }) {
  const m = COMMERCIAL_META[value] ?? COMMERCIAL_META.ok;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: m.color }}>
      <span style={{ display: 'inline-flex' }} aria-hidden>{m.icon}</span>
      {m.label}
    </span>
  );
}

export function SbomSettings() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.getSbom(), [api]);
  const [exporting, setExporting] = useState(false);

  async function exportPdf() {
    setExporting(true);
    try {
      const { blob, filename } = await api.downloadSbomPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  if (loading) return <Spinner label="Lade SBOM…" />;
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error.message}</MessageBarBody>
      </MessageBar>
    );
  }
  if (!data) return null;

  const sum = data.scan.summary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {data.domain} · Version {data.version} · Stand {new Date(data.generatedAt).toLocaleDateString('de-DE')}
        </Caption1>
        <div style={{ flex: 1 }} />
        <Button appearance="primary" icon={exporting ? <Spinner size="tiny" /> : <ArrowDownloadRegular />} disabled={exporting} onClick={exportPdf}>
          Als PDF exportieren
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: tokens.borderRadiusMedium,
          background: data.noKnownCritical ? '#e7f4ea' : '#fdeaea',
          border: `1px solid ${data.noKnownCritical ? '#3a9b58' : '#d13438'}`,
        }}
      >
        <ShieldCheckmarkFilled style={{ color: data.noKnownCritical ? '#1e7d34' : '#d13438', fontSize: 22 }} aria-hidden />
        <div>
          <Text weight="semibold" style={{ display: 'block' }}>
            {data.noKnownCritical
              ? 'Keine bekannten kritischen Schwachstellen zum Deployment-Stand.'
              : `${sum.critical} bekannte kritische Schwachstelle(n) zum Deployment-Stand.`}
          </Text>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Scan beim Deployment{data.scan.scannedAt ? ` am ${new Date(data.scan.scannedAt).toLocaleString('de-DE')}` : ''} ·
            Kritisch {sum.critical} · Hoch {sum.high} · Mittel {sum.moderate} · Niedrig {sum.low}
          </Caption1>
        </div>
      </div>

      {data.categories.map((cat) => (
        <div key={cat.title}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px' }}>
            <span style={{ fontSize: 20, display: 'inline-flex', color: tokens.colorBrandForeground1 }} aria-hidden>
              {ICONS[cat.iconKey] ?? <CodeRegular />}
            </span>
            <Text weight="semibold">{cat.title}</Text>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>({cat.items.length})</Caption1>
          </div>
          <div style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden' }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: tokens.colorNeutralBackground2 }}>
                  <th style={th}>Komponente</th>
                  <th style={th}>Zweck</th>
                  <th style={th}>Version</th>
                  <th style={th}>Lizenz</th>
                  <th style={th}>Kommerzielle Nutzung</th>
                </tr>
              </thead>
              <tbody>
                {cat.items.map((c: SbomComponent) => (
                  <tr key={c.name} style={{ borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
                    <td style={td}>
                      <Text weight="semibold" size={200}>{c.name}</Text>
                    </td>
                    <td style={td}>{c.purpose}</td>
                    <td style={{ ...td, color: tokens.colorNeutralForeground3 }}>{c.version ?? '—'}</td>
                    <td style={td}>
                      <span style={{ fontFamily: tokens.fontFamilyMonospace, fontSize: 12 }}>{c.license}</span>
                    </td>
                    <td style={td}>
                      <CommercialCell value={c.commercial} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px' }}>
          <span style={{ fontSize: 20, display: 'inline-flex', color: tokens.colorBrandForeground1 }} aria-hidden>
            <ShieldCheckmarkFilled />
          </span>
          <Text weight="semibold">Bekannte Schwachstellen</Text>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>({data.scan.items.length})</Caption1>
        </div>
        {data.scan.items.length === 0 ? (
          <Text>Keine bekannten Schwachstellen zum Scan-Zeitpunkt.</Text>
        ) : (
          <div style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden' }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '44%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: tokens.colorNeutralBackground2 }}>
                  <th style={th}>Ökosystem</th>
                  <th style={th}>Paket</th>
                  <th style={th}>Version</th>
                  <th style={th}>Schweregrad</th>
                  <th style={th}>Erläuterung</th>
                </tr>
              </thead>
              <tbody>
                {data.scan.items.map((v: SbomVuln, i) => (
                  <tr key={`${v.package}-${i}`} style={{ borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
                    <td style={td}>{v.ecosystem}</td>
                    <td style={td}><span style={{ fontFamily: tokens.fontFamilyMonospace, fontSize: 12 }}>{v.package}</span></td>
                    <td style={{ ...td, color: tokens.colorNeutralForeground3 }}>{v.version}</td>
                    <td style={td}>
                      <Badge appearance="tint" color={SEV_COLOR[v.severity] ?? 'subtle'}>{SEV_LABEL[v.severity] ?? v.severity}</Badge>
                    </td>
                    <td style={td}>
                      {v.note || '—'}
                      {v.advisory ? (
                        <>
                          {' '}
                          <Link href={v.advisory} target="_blank" rel="noreferrer">Advisory</Link>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        Versionsstände aus den Projekt-Manifesten; Schwachstellen aus dem Deployment-Scan (NuGet · npm).
      </Caption1>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--colorNeutralForeground2)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top', overflowWrap: 'anywhere' };
